import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Legend, LineChart } from './charts';
import { PathChain } from './diagrams';
import { Caption, Foot, fmt, Grid, Icon, Kpi, Note, Panel, Pill, plural, Row, Table } from './parts';
import {
    BUSES, BUS_COLOR, clock, eventsFor, liveHistory, pathPowers, SCENARIOS, TIME_COMPRESSION
} from './model';
import { alarmLogRows, download, downloadCsv } from './exports';
import { buildReport, reportRows } from './reports';
import { demoLink, SCREENS } from './screens';

const SEVERITY = { critical: { tone: 'crit', label: 'Critical' }, advisory: { tone: 'warn', label: 'Advisory' } };

export const SeverityPill = ({ severity }) => <Pill tone={SEVERITY[severity].tone}>{SEVERITY[severity].label}</Pill>;

/* ── Site overview ──────────────────────────────────────────── */

export function Site({ sim }) {
    const { dc, now, anchor, openFeeder, openMeter, setView } = sim;

    const powers = { A: pathPowers('A', dc), B: pathPowers('B', dc) };
    const loadKw = powers.A.pdu + powers.B.pdu;
    const supplyKw = powers.A.incomer + powers.B.incomer;
    const critical = dc.conditions.filter(c => c.severity === 'critical').length;
    const eventCount = BUSES.reduce((total, bus) => total + eventsFor(bus, anchor, dc.scenario).length, 0);

    const backup = Math.min(...BUSES.map(bus => (dc.buses[bus].battery.usableKwh / dc.totalKw) * 60));
    const hottest = dc.feeders.filter(f => !f.stale).reduce((top, f) => (f.temp > top.temp ? f : top));
    const lowest = BUSES.reduce((low, bus) => (dc.buses[bus].insulation < dc.buses[low].insulation ? bus : low));
    const online = BUSES.reduce((total, bus) => total + dc.buses[bus].rectifier.online, 0);

    const series = BUSES.map(bus => ({
        id: bus,
        label: `Bus ${bus}`,
        color: BUS_COLOR[bus],
        points: liveHistory(bus, dc).map(p => [p.t, p.kw / 1000])
    }));
    const points = series[0].points;
    const xTicks = [0, 0.25, 0.5, 0.75, 1].map(f => points[Math.round((points.length - 1) * f)][0]);

    return (
        <>
            <div className="dash-kpis dash-kpis--six">
                <Kpi label="Protected load" value={fmt(loadKw / 1000, 3)} unit="MW" note={`A ${fmt(powers.A.pdu, 0)} kW · B ${fmt(powers.B.pdu, 0)} kW`} />
                <Kpi label="Redundancy" value={dc.redundant ? '2N' : 'Reduced'} note={dc.redundant ? 'Both paths normal' : 'One path is not normal'} tag={dc.redundant ? <Pill tone="ok">Normal</Pill> : <Pill tone="crit">At risk</Pill>} />
                <Kpi label="Surviving-path use" value={fmt(dc.survivingUse)} unit="%" note={`${fmt(dc.headroomKw / 1000, 2)} MW headroom`} />
                <Kpi label="Backup, whole load" value={fmt(backup)} unit="min" note="Weakest battery, one path" />
                <Kpi label="Active alarms" value={dc.conditions.length} note={`${critical} critical`} tag={critical > 0 ? <Pill tone="crit">Act</Pill> : <Pill tone="warn">Review</Pill>} />
                <Kpi label="Utility to rack" value={supplyKw > 0 ? fmt((loadKw / supplyKw) * 100) : '—'} unit={supplyKw > 0 ? '%' : ''} note="Path efficiency, including other loads" />
            </div>

            <Panel title="Power chain" sub="From the utility connection to the rack, on both independent paths">
                <Foot>Successive stages observe the same power as it passes through, so they are not added together as separate loads. The gap between the incomer and the rectifier input is other load fed ahead of the UPS.</Foot>
                <PathChain bus="A" dc={dc} onOpen={openMeter} />
                <PathChain bus="B" dc={dc} onOpen={openMeter} />
                <Caption>Select a stage to open its meter</Caption>
            </Panel>

            <Grid cols="minmax(0, 1.25fr) minmax(0, 1fr)">
                <Panel
                    title="Active alarms"
                    sub="Worked out from the live readings"
                    actions={<button type="button" className="dash-link" onClick={() => setView('protection')}>All alarms →</button>}
                >
                    {dc.conditions.length === 0 && <Note>No active alarms.</Note>}

                    <ul className="dash-alerts">
                        {dc.conditions.map(condition => (
                            <li key={condition.id}>
                                <button
                                    type="button"
                                    className={`is-${condition.severity}`}
                                    onClick={() => (condition.feeder ? openFeeder(condition.feeder) : setView('protection'))}
                                >
                                    <span className="dash-alerts__top">
                                        <strong>{condition.asset} · {condition.short}</strong>
                                        <SeverityPill severity={condition.severity} />
                                    </span>
                                    <span>{condition.summary}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </Panel>

                <Panel title="At a glance" sub="The worst reading in each area">
                    <Row label="Hottest connection" note={`${hottest.joint} · ${hottest.location}`}>
                        <Link className="dash-glance" to={demoLink('thermal')}>{fmt(hottest.temp)} °C</Link>
                    </Row>
                    <Row label="Lowest insulation" note={`Bus ${lowest} · insulation monitor`}>
                        <Link className="dash-glance" to={demoLink('protection')}>{fmt(dc.buses[lowest].insulation, 2)} MΩ</Link>
                    </Row>
                    <Row label="Rectifier modules" note="Across both paths">
                        <Link className="dash-glance" to={demoLink('energy')}>{online}/18 online</Link>
                    </Row>
                    <Row label="Power quality events" note="Both paths, trailing 24 hours">
                        <Link className="dash-glance" to={demoLink('events')}>{eventCount}</Link>
                    </Row>
                    <Row label="Sensor coverage" note="J06-B temperature is stale">
                        <Link className="dash-glance" to={demoLink('health')}>15/16 thermal</Link>
                    </Row>
                </Panel>
            </Grid>

            <Panel title="Power demand" sub="Trailing 24 hours · Eastern time">
                <Legend items={series.map(s => ({ label: s.label, color: s.color }))} />
                <LineChart
                    series={series}
                    height={220}
                    label="DC bus power on both paths over the last 24 hours"
                    xTicks={xTicks}
                    formatX={clock}
                    formatY={value => value.toLocaleString('en-US')}
                    formatTip={value => `${fmt(value, 3)} MW`}
                    tipTitle={value => `${clock(value)} ET`}
                    yLabel="MW"
                />
                <Caption>15-minute demand {fmt(dc.demandKw / 1000, 3)} MW · 24-hour import {fmt(dc.importKwh / 1000, 2)} MWh · Updated {clock(now)} ET</Caption>
            </Panel>
        </>
    );
}

/* ── Fault simulator ────────────────────────────────────────── */

const titleOf = id => SCREENS.find(item => item.id === id).title;

export function Simulator({ sim }) {
    const { dc, scenario, startScenario, stopScenario, alarmLog, notify } = sim;

    const exportLog = () => {
        downloadCsv('SmartDC_Demo_Alarm_Log.csv', alarmLogRows(alarmLog));
        notify('Alarm log exported as a CSV, marked simulated.');
    };

    return (
        <>
            <Panel title="Choose a fault" sub={`Simulated, with battery discharge running ${TIME_COMPRESSION}× faster than real time`}>
                <Foot>Faults are injected into the simulation running in this browser, and nothing here touches any equipment. The system only watches: it does not open breakers or switch anything, and the installed protection is what acts on a real fault.</Foot>

                <ul className="dash-faults">
                    {SCENARIOS.map(item => {
                        const running = scenario && scenario.id === item.id;

                        return (
                            <li className={running ? 'is-running' : undefined} key={item.id}>
                                <div>
                                    <span className="dash-eyebrow">{item.where}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.text}</p>
                                    <p className="dash-faults__watch">
                                        Watch:{' '}
                                        {item.watch.map((view, i) => (
                                            <React.Fragment key={view}>
                                                {i > 0 && ' · '}
                                                <Link className="dash-link" to={demoLink(view)}>{titleOf(view)}</Link>
                                            </React.Fragment>
                                        ))}
                                    </p>
                                </div>

                                {running ? (
                                    <button type="button" className="dash-btn" onClick={stopScenario}>Return to normal</button>
                                ) : (
                                    <button type="button" className="dash-btn dash-btn--primary" onClick={() => startScenario(item.id)}>
                                        <Icon name="simulator" />
                                        Run this fault
                                    </button>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </Panel>

            <Panel
                title="What the system did"
                sub="Every alarm raised and cleared in this session"
                badge={<Pill>{plural(dc.conditions.length, 'active alarm')}</Pill>}
                actions={<button type="button" className="dash-btn" onClick={exportLog} disabled={alarmLog.length === 0}>Export log</button>}
            >
                {alarmLog.length === 0 ? (
                    <Note>Nothing has changed yet. Run a fault, and each alarm appears here as the readings cross a limit.</Note>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th scope="col">Time</th>
                                <th scope="col">Asset</th>
                                <th scope="col">Alarm</th>
                                <th scope="col">State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {alarmLog.map(row => (
                                <tr key={`${row.at}-${row.asset}-${row.event}-${row.state}`}>
                                    <th scope="row">{clock(row.at)} ET</th>
                                    <td>{row.asset}</td>
                                    <td>{row.event}</td>
                                    <td><Pill tone={row.state === 'Escalated' ? 'crit' : row.state === 'Raised' ? (row.severity === 'critical' ? 'crit' : 'warn') : 'ok'}>{row.state}</Pill></td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Panel>
        </>
    );
}

/* ── Reports ────────────────────────────────────────────────── */

export function Reports({ sim }) {
    const { dc, anchor, alarmLog, notify } = sim;

    const events = useMemo(
        () => BUSES.flatMap(bus => eventsFor(bus, anchor, dc.scenario)).sort((x, y) => y.at - x.at),
        [anchor, dc.scenario.id] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const report = buildReport(dc, events, alarmLog);

    const exportCsv = () => {
        downloadCsv('SmartDC_Demo_Daily_Report.csv', reportRows(report));
        notify('Report exported as a CSV, marked simulated.');
    };

    const exportJson = () => {
        download('SmartDC_Demo_Daily_Report.json', JSON.stringify({ dataMode: 'SIMULATED', ...report }, null, 2), 'application/json');
        notify('Report exported as JSON, marked simulated.');
    };

    return (
        <div className="dash-report">
            <Panel
                title={report.title}
                sub={`${report.site} · ${report.period} · Generated ${report.generated}`}
                actions={(
                    <>
                        <button type="button" className="dash-btn" onClick={() => window.print()}>
                            <Icon name="printer" />
                            Print or save PDF
                        </button>
                        <button type="button" className="dash-btn" onClick={exportCsv}>CSV</button>
                        <button type="button" className="dash-btn" onClick={exportJson}>JSON</button>
                    </>
                )}
            >
                <p className="dash-report__notice">{report.notice}</p>

                <dl className="dash-readout">
                    {report.summary.map(item => (
                        <div key={item.label}>
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                        </div>
                    ))}
                </dl>
            </Panel>

            <Panel title="By path">
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Measurement</th>
                            <th scope="col" className="is-num">Path A</th>
                            <th scope="col" className="is-num">Path B</th>
                            <th scope="col" className="is-num">Unit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.paths.map(row => (
                            <tr key={row.label}>
                                <th scope="row">{row.label}</th>
                                <td className="is-num">{row.a}</td>
                                <td className="is-num">{row.b}</td>
                                <td className="is-num is-unit">{row.unit}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Panel>

            <Panel title="Hottest connections">
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Joint</th>
                            <th scope="col">Location</th>
                            <th scope="col" className="is-num">Temperature</th>
                            <th scope="col" className="is-num">Rise</th>
                            <th scope="col" className="is-num">Load</th>
                            <th scope="col" className="is-num">R estimate</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.joints.map(row => (
                            <tr key={row.joint}>
                                <th scope="row">{row.joint}</th>
                                <td>{row.location}</td>
                                <td className="is-num">{row.temp}</td>
                                <td className="is-num">{row.rise}</td>
                                <td className="is-num">{row.amps}</td>
                                <td className="is-num">{row.resistance}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Panel>

            <Panel title="Power quality events">
                <Table>
                    <thead>
                        <tr>
                            <th scope="col">Time</th>
                            <th scope="col">Type</th>
                            <th scope="col">Event</th>
                            <th scope="col">Where</th>
                            <th scope="col" className="is-num">Magnitude</th>
                            <th scope="col" className="is-num">Duration</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.events.map((row, i) => (
                            <tr key={`${row.time}-${row.where}-${i}`}>
                                <th scope="row">{row.time}</th>
                                <td>{row.type}</td>
                                <td>{row.title}</td>
                                <td>{row.where}</td>
                                <td className="is-num">{row.size}</td>
                                <td className="is-num">{row.duration}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Panel>

            <Panel title="Alarm log" sub="Raised and cleared in this session">
                {report.alarms.length === 0 ? (
                    <Note>No alarms have been raised or cleared in this session.</Note>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th scope="col">Time</th>
                                <th scope="col">Asset</th>
                                <th scope="col">Alarm</th>
                                <th scope="col">State</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.alarms.map((row, i) => (
                                <tr key={`${row.time}-${row.asset}-${i}`}>
                                    <th scope="row">{row.time}</th>
                                    <td>{row.asset}</td>
                                    <td>{row.event}</td>
                                    <td>{row.state}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
            </Panel>
        </div>
    );
}
